import { computed } from "vue";
import {
    createBlock,
    headingField,
    separateHeadingFormattingField,
    htmlField,
    markdownField,
    separateParagraphFormattingField,
    separateMediaFormattingField,
    separateTestimonialFormattingField,
    separateSimpleListFormattingField,
    separateButtonsLayoutField,
    arrayField,
    separateInfoNoteFormattingField,
    buttonField,
    testimonialField,
    mediaField,
} from "./blockBuilder";
import type { BlockDef } from "./block.interfaces";
import { useDefaultColors } from "./useDefaultColors";
import { useFieldConfig } from "./useFieldConfig";
import { useFieldOptions } from "./useFieldOptions";
import {
    HeadingElement,
    ParagraphElement,
    MediaElement,
    TestimonialElement,
    SimpleListElement,
    InfoNoteElement,
    ButtonsElement,
    MarkdownElement,
    type HeadingElementProps,
    type ParagraphElementProps,
    type MediaElementProps,
    type TestimonialElementProps,
    type SimpleListElementProps,
    type InfoNoteElementProps,
    type ButtonsElementProps,
    type MarkdownElementProps,
} from "@/views/blocks/elements/elements";
import { MediaType } from "@/views/blocks/PageBlock.interface";
import { useI18n } from "vue-i18n";
import {
    getStockPhotoUrl,
    AVATAR_API_BASE_URL,
    ICON_API_BASE_URL,
} from "@/apps/page/services/page.service";
import defaultVideoUrl from "@/views/blocks/shared/videos/default-video.mp4";

export function useElements() {
    const { t } = useI18n();
    const { colorDefaults } = useDefaultColors();
    const { translations } = useFieldConfig();
    const {
        headingTextSizeOptions,
        textAlignOptions,
        maxWidthOptions,
        elementAlignOptions,
        paddingTopOptions,
        paddingBottomOptions,
        paragraphTextSizeOptions,
        flexJustifyOptions,
    } = useFieldOptions();

    const headingElementBlock = computed(() =>
        createBlock<HeadingElementProps>({
            typeId: "HeadingElement",
            type: "elements",
            name: t("page.blocks.elements.heading"),
            component: HeadingElement,
        })
            .withField(
                "heading",
                headingField(translations.value.heading, "Build something amazing together", "h2", {
                    text: translations.value.text,
                    tag: translations.value.tag,
                })
            )
            .withField(
                "formatting",
                separateHeadingFormattingField(
                    translations.value.formatting,
                    "text-4xl sm:text-5xl",
                    "text-left",
                    "max-w-2xl",
                    "mx-auto",
                    "pt-4",
                    "pb-4",
                    {
                        textSize: translations.value.textSize,
                        textAlign: translations.value.textAlign,
                        maxWidth: translations.value.maxWidth,
                        elementAlign: translations.value.elementAlign,
                        paddingTop: translations.value.paddingTop,
                        paddingBottom: translations.value.paddingBottom,
                    },
                    {
                        textSize: headingTextSizeOptions.value,
                        textAlign: textAlignOptions.value,
                        maxWidth: maxWidthOptions.value,
                        elementAlign: elementAlignOptions.value,
                        paddingTop: paddingTopOptions.value,
                        paddingBottom: paddingBottomOptions.value,
                    }
                )
            )
            .withStyleFields([colorDefaults.value.background, colorDefaults.value.text])
            .build()
    );

    const paragraphElementBlock = computed(() =>
        createBlock<ParagraphElementProps>({
            typeId: "ParagraphElement",
            type: "elements",
            name: t("page.blocks.elements.paragraph"),
            component: ParagraphElement,
        })
            .withField(
                "paragraph",
                htmlField(
                    translations.value.paragraph,
                    "Connect with like-minded people, share experiences, and create lasting memories. Join our vibrant community where friendships flourish and great ideas come to life.",
                    translations.value.text,
                    5
                )
            )
            .withField(
                "formatting",
                separateParagraphFormattingField(
                    translations.value.formatting,
                    "text-lg",
                    "text-left",
                    "max-w-2xl",
                    "mx-auto",
                    "pt-0",
                    "pb-4",
                    {
                        textSize: translations.value.textSize,
                        textAlign: translations.value.textAlign,
                        maxWidth: translations.value.maxWidth,
                        elementAlign: translations.value.elementAlign,
                        paddingTop: translations.value.paddingTop,
                        paddingBottom: translations.value.paddingBottom,
                    },
                    {
                        textSize: paragraphTextSizeOptions.value,
                        textAlign: textAlignOptions.value,
                        maxWidth: maxWidthOptions.value,
                        elementAlign: elementAlignOptions.value,
                        paddingTop: paddingTopOptions.value,
                        paddingBottom: paddingBottomOptions.value,
                    }
                )
            )
            .withStyleFields([colorDefaults.value.background, colorDefaults.value.text])
            .build()
    );

    const markdownElementBlock = computed(() =>
        createBlock<MarkdownElementProps>({
            typeId: "MarkdownElement",
            type: "elements",
            name: t("page.blocks.elements.markdown"),
            component: MarkdownElement,
        })
            .withField(
                "markdown",
                markdownField(
                    translations.value.markdown,
                    `# Welcome to Our Community

Discover a place where you can connect with others, share your passions, and grow together. Our platform offers a variety of features to help you get started.

- **Engage with content:** Explore articles, discussions, and resources.
- **Join groups:** Find like-minded people and collaborate on projects.
- **Attend events:** Participate in workshops and webinars to expand your skills.

[Get Started](#)`,
                    translations.value.markdown,
                    10
                )
            )
            .withField(
                "formatting",
                separateParagraphFormattingField(
                    translations.value.formatting,
                    "text-base",
                    "text-left",
                    "max-w-2xl",
                    "mx-auto",
                    "pt-4",
                    "pb-4",
                    {
                        textSize: translations.value.textSize,
                        textAlign: translations.value.textAlign,
                        maxWidth: translations.value.maxWidth,
                        elementAlign: translations.value.elementAlign,
                        paddingTop: translations.value.paddingTop,
                        paddingBottom: translations.value.paddingBottom,
                    },
                    {
                        textSize: paragraphTextSizeOptions.value,
                        textAlign: textAlignOptions.value,
                        maxWidth: maxWidthOptions.value,
                        elementAlign: elementAlignOptions.value,
                        paddingTop: paddingTopOptions.value,
                        paddingBottom: paddingBottomOptions.value,
                    }
                )
            )
            .withStyleFields([colorDefaults.value.background, colorDefaults.value.text])
            .build()
    );

    const mediaElementBlock = computed(() =>
        createBlock<MediaElementProps>({
            typeId: "MediaElement",
            type: "elements",
            name: t("page.blocks.elements.media"),
            component: MediaElement,
        })
            .withField(
                "media",
                mediaField(
                    translations.value.media,
                    {
                        src: getStockPhotoUrl(
                            "communities",
                            "silhouette-business-people-discussion-meeting-cityscape-team-concept.webp"
                        ),
                        alt: "Silhouette business people discussion meeting cityscape",
                        mediaType: MediaType.IMAGE,
                        videoSrc: defaultVideoUrl,
                        videoAutoplay: false,
                        videoControls: true,
                        videoLoop: false,
                        iframeSrc: "https://www.youtube.com/embed/LxIR7eFw8HI?si=ObbVhSBAi35VMABl",
                    },
                    {
                        mediaType: translations.value.mediaType,
                        src: translations.value.src,
                        alt: translations.value.alt,
                        videoSrc: translations.value.videoSrc,
                        videoAutoplay: translations.value.videoAutoplay,
                        videoControls: translations.value.videoControls,
                        videoLoop: translations.value.videoLoop,
                        iframeSrc: translations.value.iframeSrc,
                    }
                )
            )
            .withField(
                "formatting",
                separateMediaFormattingField(
                    translations.value.formatting,
                    "max-w-2xl",
                    "mx-auto",
                    "pt-4",
                    "pb-4",
                    {
                        maxWidth: translations.value.maxWidth,
                        elementAlign: translations.value.elementAlign,
                        paddingTop: translations.value.paddingTop,
                        paddingBottom: translations.value.paddingBottom,
                        iframeHeight: translations.value.iframeHeight,
                    },
                    {
                        maxWidth: maxWidthOptions.value,
                        elementAlign: elementAlignOptions.value,
                        paddingTop: paddingTopOptions.value,
                        paddingBottom: paddingBottomOptions.value,
                    }
                )
            )
            .withStyleFields([colorDefaults.value.background])
            .build()
    );

    const testimonialElementBlock = computed(() =>
        createBlock<TestimonialElementProps>({
            typeId: "TestimonialElement",
            type: "elements",
            name: t("page.blocks.elements.testimonial"),
            component: TestimonialElement,
        })
            .withField(
                "testimonial",
                testimonialField(
                    translations.value.testimonial,
                    {
                        text: "This course transformed my career path completely. The practical projects helped me build a portfolio that landed me a job as a junior data analyst. The instructors were incredibly supportive and the community of learners provided valuable feedback on my work.",
                        personImageSrc: `${AVATAR_API_BASE_URL}/m/1`,
                        personName: "David Chen",
                        personPosition: "Data Analyst",
                    },
                    {
                        text: translations.value.text,
                        personImageSrc: translations.value.iconSrc,
                        personName: translations.value.name,
                        personPosition: translations.value.role,
                    }
                )
            )
            .withField(
                "formatting",
                separateTestimonialFormattingField(
                    translations.value.formatting,
                    "text-base",
                    "max-w-2xl",
                    "mx-auto",
                    "pt-4",
                    "pb-4",
                    {
                        textSize: translations.value.textSize,
                        maxWidth: translations.value.maxWidth,
                        elementAlign: translations.value.elementAlign,
                        paddingTop: translations.value.paddingTop,
                        paddingBottom: translations.value.paddingBottom,
                    },
                    {
                        textSize: paragraphTextSizeOptions.value,
                        maxWidth: maxWidthOptions.value,
                        elementAlign: elementAlignOptions.value,
                        paddingTop: paddingTopOptions.value,
                        paddingBottom: paddingBottomOptions.value,
                    }
                )
            )
            .withStyleFields([
                colorDefaults.value.background,
                colorDefaults.value.text,
                colorDefaults.value.brand,
            ])
            .build()
    );

    const simpleListElementBlock = computed(() =>
        createBlock<SimpleListElementProps>({
            typeId: "SimpleListElement",
            type: "elements",
            name: t("page.blocks.elements.simpleList"),
            component: SimpleListElement,
        })
            .withField(
                "formatting",
                separateSimpleListFormattingField(
                    translations.value.formatting,
                    "text-base",
                    "max-w-2xl",
                    "mx-auto",
                    "pt-4",
                    "pb-4",
                    {
                        textSize: translations.value.textSize,
                        maxWidth: translations.value.maxWidth,
                        elementAlign: translations.value.elementAlign,
                        paddingTop: translations.value.paddingTop,
                        paddingBottom: translations.value.paddingBottom,
                    },
                    {
                        textSize: paragraphTextSizeOptions.value,
                        maxWidth: maxWidthOptions.value,
                        elementAlign: elementAlignOptions.value,
                        paddingTop: paddingTopOptions.value,
                        paddingBottom: paddingBottomOptions.value,
                    }
                )
            )
            .withArrayField(
                "items",
                arrayField(
                    translations.value.item,
                    "items",
                    {
                        iconSrc: {
                            label: translations.value.iconSrc,
                            type: "image",
                        },
                        title: {
                            label: translations.value.title,
                            type: "text",
                        },
                        description: {
                            label: translations.value.description,
                            type: "text",
                        },
                    },
                    [
                        {
                            iconSrc: `${ICON_API_BASE_URL}/development/code-01.svg`,
                            title: "Libraries:",
                            description:
                                "Master NumPy, Pandas, Matplotlib, Seaborn, and Scikit-learn for data science fundamentals.",
                        },
                        {
                            iconSrc: `${ICON_API_BASE_URL}/charts/line-chart-up-01.svg`,
                            title: "Analysis:",
                            description:
                                "Learn data cleaning, transformation, statistical methods, and visualization techniques.",
                        },
                        {
                            iconSrc: `${ICON_API_BASE_URL}/development/cpu-chip-01.svg`,
                            title: "Models:",
                            description:
                                "Build regression, decision trees, random forests, SVMs, clustering, and neural network models.",
                        },
                    ],
                    {
                        iconSrc: `${ICON_API_BASE_URL}/general/plus.svg`,
                        title: "New capability",
                        description: "Enhanced functionality description",
                    }
                )
            )
            .withStyleFields([colorDefaults.value.background, colorDefaults.value.text])
            .build()
    );

    const infoNoteElementBlock = computed(() =>
        createBlock<InfoNoteElementProps>({
            typeId: "InfoNoteElement",
            type: "elements",
            name: t("page.blocks.elements.infoNote"),
            component: InfoNoteElement,
        })
            .withField(
                "infoNote",
                htmlField(
                    translations.value.infoNote,
                    "This is an informational note that provides additional context or guidance.",
                    translations.value.text,
                    3
                )
            )
            .withField(
                "formatting",
                separateInfoNoteFormattingField(
                    translations.value.formatting,
                    "text-sm",
                    "justify-start",
                    "max-w-2xl",
                    "mx-auto",
                    "pt-0",
                    "pb-4",
                    {
                        textSize: translations.value.textSize,
                        justifyContent: translations.value.justifyContent,
                        maxWidth: translations.value.maxWidth,
                        elementAlign: translations.value.elementAlign,
                        paddingTop: translations.value.paddingTop,
                        paddingBottom: translations.value.paddingBottom,
                    },
                    {
                        textSize: paragraphTextSizeOptions.value,
                        justifyContent: flexJustifyOptions.value,
                        maxWidth: maxWidthOptions.value,
                        elementAlign: elementAlignOptions.value,
                        paddingTop: paddingTopOptions.value,
                        paddingBottom: paddingBottomOptions.value,
                    }
                )
            )
            .withStyleFields([colorDefaults.value.background, colorDefaults.value.text])
            .build()
    );

    const buttonsElementBlock = computed(() =>
        createBlock<ButtonsElementProps>({
            typeId: "ButtonsElement",
            type: "elements",
            name: t("page.blocks.elements.buttons"),
            component: ButtonsElement,
        })
            .withField(
                "primaryButton",
                buttonField(
                    translations.value.primaryButton,
                    {
                        label: "Get started",
                        href: "#",
                    },
                    {
                        label: translations.value.label,
                        href: translations.value.href,
                    }
                )
            )
            .withField(
                "secondaryButton",
                buttonField(
                    translations.value.secondaryButton,
                    {
                        label: "Learn more",
                        href: "#",
                    },
                    {
                        label: translations.value.label,
                        href: translations.value.href,
                    }
                )
            )
            .withField(
                "formatting",
                separateButtonsLayoutField(
                    translations.value.formatting,
                    "text-sm",
                    "max-w-2xl",
                    "mx-auto",
                    "pt-4",
                    "pb-4",
                    "justify-start",
                    {
                        textSize: translations.value.textSize,
                        maxWidth: translations.value.maxWidth,
                        elementAlign: translations.value.elementAlign,
                        paddingTop: translations.value.paddingTop,
                        paddingBottom: translations.value.paddingBottom,
                        justifyContent: translations.value.justifyContent,
                    },
                    {
                        textSize: paragraphTextSizeOptions.value,
                        maxWidth: maxWidthOptions.value,
                        elementAlign: elementAlignOptions.value,
                        paddingTop: paddingTopOptions.value,
                        paddingBottom: paddingBottomOptions.value,
                        justifyContent: flexJustifyOptions.value,
                    }
                )
            )
            .withStyleFields([
                colorDefaults.value.background,
                colorDefaults.value.text,
                colorDefaults.value.brand,
            ])
            .build()
    );

    const blocks = computed<BlockDef[]>(() => [
        headingElementBlock.value,
        paragraphElementBlock.value,
        markdownElementBlock.value,
        mediaElementBlock.value,
        infoNoteElementBlock.value,
        simpleListElementBlock.value,
        testimonialElementBlock.value,
        buttonsElementBlock.value,
    ]);

    return {
        blocks,
    };
}
